import { isEqual } from 'lodash';
import { Patch } from 'rfc6902';
import applyPatch from './applyPatch';

interface RebasePatchResult<T> {
  /**
   * The new base state.
   */
  base: T;
  /**
   * The new "local" state, or null on conflict.
   */
  state: T | null;
}

/**
 * Attempts to merge two patches for the same base state, i.e. when editing a DOLAN
 * and some other user introduces simultaneous edits to the same DOLAN.
 * This is actually similar to GIT rebase operation, therefore using the term "rebase" here.
 *
 * For the base state, we assume two patches:
 * - the "upstream patch", i.e. incoming changes done by other users
 * - the "local patch", i.e. the changes to be "reapplied" on top of incoming changes
 *
 * The result is a new base state (with incoming upstream patch applied to it) and
 * the new local state assuming that those local changes could still be applied to
 * the new base without a conflict. On conflict the returned new local state will be null.
 */
export default function rebasePatch<T>(base: T, upstreamPatch: Patch, localPatch: Patch): RebasePatchResult<T> {
  const newBaseState: T = applyPatch(base, upstreamPatch);
  const localState: T = applyPatch(base, localPatch);
  let newState: T | null;
  try {
    const state1: T = applyPatch(newBaseState, localPatch);
    const state2: T = applyPatch(localState, upstreamPatch);
    // The two states must be equal
    newState = isEqual(state1, state2) ? state1 : null;
  } catch {
    // Error applying patches for the new local state -> conflict
    newState = null;
  }
  return {
    base: newBaseState,
    state: newState,
  };
}
